﻿import { Video } from '../video';
import { Modal } from 'bootstrap.native';
import getTransitionDuration from 'get-transition-duration';
import { event } from '../../Foundation';

/** Class for handling a modal with a video playing in it.
 * Using the attribute [data-video-lightbox] containing the url of the video to be played.
 *
 * The allowed video urls are: internal (sitecore/HTML5), youtube.
 *
 * Optional parameters can be passed as well, like: autoplay, loop, muted, allowfullscreen and more.
 */
export class VideoLightbox {
    /**
     * @param {HTMLElement} element
     */
    constructor(element) {
        element.VideoLightbox = this;

        this._refs = {
            el: element,
            modal: document.querySelector('#video-lightbox')
        };

        this._data = {
            url: this._refs.el.dataset.videoLightbox
        };

        // If no url is provided, we'll cancel any further operations
        if (this._data.url === '') return false;

        // Engage Modal controller eg.
        this.modal = this._refs.modal.Modal || new Modal(this._refs.modal);
        this._data.isYoutubeVideo = this._data.url.includes('youtube') || this._data.url.includes('youtu.be');
        this._data.isTwentryThreeVideo = this._data.url.includes('gnresound.tv') || this._data.url.includes('interton.tv') || this._data.url.includes('beltone.tv');
        this._onModalClose = this._onModalClose.bind(this);

        // # Event management
        this._refs.el.addEventListener('click', (e) => {
            // Block default behavior in case el is a a-tag 
            if (this._refs.el.tagName === "A") {
                e.preventDefault();
            }

            // Update modal with content
            this.modal.setContent(this.view);
            this.modal.toggle();

            setTimeout(() => {
                this.videoInstance = new Video(this._refs.modal.querySelector('.video'));
                if(this._refs.modal) {
                    this._refs.modal.addEventListener('hidden.bs.modal', this._onModalClose);
                }
            }, getTransitionDuration(this._refs.modal, true));
        });
    }

    /**
    * Get a custom-view for displaying video
    * TODO: Refactor autoplay in phase 2 of hassium
    * @returns {HTMLElement}
    */
    get view() {
        if (this._data.isYoutubeVideo) {
            // Append Autoplay
            this._data.url += (this._data.url.includes("?") ? "&" : "?") + "autoplay=1";
            return this.getYoutube({ url: this._data.url });
        } else if (this._data.isTwentryThreeVideo) {
            return this.getVideoTwentyThree({ url: this._data.url });
        }

        return this.getInternal({ url: this._data.url });
    } 

    /**
    * Reflow video instance and execute cleanup
    */
    _onModalClose() {
        // Reset instance
        this.videoInstance.togglePlay();
        this.videoInstance = null;

        this.modal.setContent('');

        //Emit event for callback management
        event.emit(document, 'hidden.bs.modal.video', {
            'url': this._data.url,
            'el': this._refs.el
        });

        // also removes the inital event listener for the before mentioned removal of the videoInstance
        this._refs.modal.removeEventListener('hidden.bs.modal', this._onModalClose);
    }

    /**
     * Get video rendering for TwentyThreeVideo
     * Video class uses classnames to determine which platform is used for playing
     * @param {Object} oData - Properties for rendering
     * @returns {String} - Markup 
     */
    getVideoTwentyThree(oData = Object) {
        return `<div class="video">
                    <div class="video__wrapper">
                        <div class="video__twentythree">
                            <div class="video__twentythree__wrapper">
                                <iframe src="${oData.url}" class="video__twentythree__video" frameborder="0" border="0" scrolling="no"></iframe>
                            </div>
                        </div>
                    </div>
                </div>`;
    }

    /**
     * Get video rendering for Youtube videos
     * Video class uses classnames to determine which platform is used for playing
     * @param {Object} oData - Properties for rendering
     * @returns {String} - Markup
     */
    getYoutube(oData = Object) {
        return `<div class="video">
                    <div class="video__wrapper">
                        <div class="video__youtube">
                            <div class="video__youtube__wrapper">
                                <div class="video__youtube__video" data-src="${oData.url}"></div>
                            </div>
                        </div>
                    </div>
                </div>`;
    }

    /**
     * Get video rendering for Internal videos
     * Video class uses classnames to determine which platform is used for playing
     * @param {Object} oData - Properties for rendering
     */
    getInternal(oData = Object) {
        return `<div class="video">
                    <div class="video__wrapper">
                        <div class="video__internal">
                            <div class="video__internal__wrapper">
                                <button type="button" class="video__internal__play-button">Play video</button>
                                <video class="video__internal__video" controls>
                                    <source src="${oData.url}" type="video/mp4">
                                </video>
                            </div>
                        </div>
                    </div>
                </div>`;
    }
}